import type { EChartsOption } from 'echarts';
import { resolveMnemonicBindingLabel } from './mnemonic-binding-label.util';

export type MnemonicChartConfig = {
    type?: string;
    data?: {
        labels?: string[];
        datasets?: Array<{
            label?: string | string[];
            data?: number[];
        }>;
    };
    options?: Record<string, unknown>;
};

export const EMPTY_CHART_CONFIG: MnemonicChartConfig = {
    type: 'line',
    data: {
        labels: [],
        datasets: [],
    },
};

export function normalizeChartDatasets(data?: MnemonicChartConfig['data']): MnemonicChartConfig['data'] {
    if (!data) {
        return { ...EMPTY_CHART_CONFIG.data };
    }

    return {
        ...data,
        datasets: data.datasets?.map((dataset) => ({
            ...dataset,
            label: resolveDatasetLabel(dataset.label),
        })),
    };
}

export function resolveDatasetLabel(label?: string | string[]): string {
    if (Array.isArray(label)) {
        return label.filter(Boolean).join(', ');
    }

    return label?.trim() ?? '';
}

export function formatMnemonicChartAxisLabel(timestamp: number | string): string {
    const date = new Date(Number(timestamp));

    if (Number.isNaN(date.getTime())) {
        return '';
    }

    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${hours}:${minutes}`;
}

export function resolveMnemonicChartSeriesName(
    object: { name?: string; variableId?: string; deviceId?: string },
    devices?: Array<{ id?: string; name?: string; defaultName?: string; variables?: Array<{ id?: string; originVariableId?: string; customName?: string; name?: string }> }>,
): string {
    return resolveMnemonicBindingLabel(object, devices as never);
}

export function buildMnemonicChartArchiveData(
    archive: Array<{ ts: number | string; value: number }>,
    seriesName: string,
): NonNullable<MnemonicChartConfig['data']> {
    return {
        labels: archive.map((point) => formatMnemonicChartAxisLabel(point.ts)),
        datasets: [
            {
                label: seriesName,
                data: archive.map((point) => point.value),
            },
        ],
    };
}

export function resolveChartSeriesNameFromConfig(config: MnemonicChartConfig, fallbackSeriesName?: string): string {
    const datasetLabel = resolveDatasetLabel(config.data?.datasets?.[0]?.label);
    const fallback = fallbackSeriesName?.trim() ?? '';

    if (/^chart\d+$/i.test(fallback)) {
        return datasetLabel || fallback;
    }

    if (fallback.includes('(')) {
        return fallback;
    }

    if (datasetLabel.includes('(')) {
        return datasetLabel;
    }

    return fallback || datasetLabel;
}

function resolveChartMetrics(width: number, height: number) {
    const scale = Math.max(1, Math.min(width / 360, height / 240));

    return {
        legendFontSize: Math.round(12 * scale),
        axisFontSize: Math.round(11 * scale),
        lineWidth: Math.max(1.5, 2 * scale),
        symbolSize: Math.round(6 * scale),
        gridLeft: Math.round(44 * scale),
        gridRight: Math.round(14 * scale),
        gridTop: Math.round(44 * scale),
        gridTopNoLegend: Math.round(24 * scale),
        gridBottom: Math.round(30 * scale),
        gridBottomRotated: Math.round(42 * scale),
    };
}

export function toEChartsOption(
    config: MnemonicChartConfig,
    fallbackSeriesName?: string,
    width = 360,
    height = 240,
): EChartsOption {
    const chartType = config.type === 'bar' ? 'bar' : 'line';
    const data = normalizeChartDatasets(config.data);
    const labels = data?.labels ?? [];
    const datasets = data?.datasets ?? [];
    const customOptions = (config.options ?? {}) as EChartsOption;
    const seriesNames = datasets.map((dataset, index) => {
        const datasetLabel = resolveDatasetLabel(dataset.label);
        const fallback = index === 0 ? fallbackSeriesName?.trim() ?? '' : '';

        return resolveChartSeriesNameFromConfig(
            { data: { datasets: [{ label: datasetLabel }] } },
            fallback,
        );
    });
    const hasLegend = seriesNames.some(Boolean);
    const metrics = resolveChartMetrics(width, height);
    const shouldRotateLabels = labels.length > 6;

    return {
        ...customOptions,
        animation: false,
        legend: {
            show: hasLegend,
            top: 4,
            left: 'center',
            data: seriesNames.filter(Boolean),
            textStyle: {
                fontSize: metrics.legendFontSize,
                color: '#171b2c',
            },
            ...(customOptions.legend as object),
        },
        grid: {
            left: metrics.gridLeft,
            right: metrics.gridRight,
            top: hasLegend ? metrics.gridTop : metrics.gridTopNoLegend,
            bottom: shouldRotateLabels ? metrics.gridBottomRotated : metrics.gridBottom,
            ...(customOptions.grid as object),
        },
        tooltip: {
            trigger: 'axis',
            ...(customOptions.tooltip as object),
        },
        xAxis: {
            type: 'category',
            data: labels,
            boundaryGap: chartType === 'bar',
            axisLabel: {
                fontSize: metrics.axisFontSize,
                color: '#5c6370',
                rotate: shouldRotateLabels ? 35 : 0,
                hideOverlap: true,
                margin: shouldRotateLabels ? 10 : 8,
            },
            axisLine: {
                lineStyle: {
                    color: '#c5cad3',
                },
            },
            ...(customOptions.xAxis as object),
        },
        yAxis: {
            type: 'value',
            scale: true,
            axisLabel: {
                fontSize: metrics.axisFontSize,
                color: '#5c6370',
            },
            splitLine: {
                lineStyle: {
                    color: '#e8ebf0',
                },
            },
            ...(customOptions.yAxis as object),
        },
        series: datasets.map((dataset, index) => ({
            name: seriesNames[index] || resolveDatasetLabel(dataset.label),
            type: chartType,
            data: dataset.data ?? [],
            showSymbol: chartType === 'line',
            symbolSize: metrics.symbolSize,
            lineStyle: {
                width: metrics.lineWidth,
            },
            smooth: false,
            animation: false,
        })),
    };
}
